// firestore/querybuilder.ts
import {
  CollectionReference,
  Query,
  QueryConstraint,
  QueryFilterConstraint,
  DocumentData,
  DocumentReference,
  query,
  where,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  orderBy,
  getDoc,
  doc,
  and as andFilter,
  or as orFilter,
  OrderByDirection,
  documentId,
} from "firebase/firestore";

import { FilterQuery, FilterFields, FilterOrderBy } from "./types";

// ---------------- Operator map / helpers ----------------

const OP_KEYS = new Set([
  "$gt",
  "$gte",
  "$lt",
  "$lte",
  "$ne",
  "$in",
  "$nin",
  "$exists",
  "$regex",
  "$arrayContains",
  "$arrayContainsAny",
] as const);

const operatorMap = {
  $gt: ">",
  $gte: ">=",
  $lt: "<",
  $lte: "<=",
  $ne: "!=",
  $in: "in",
  $nin: "not-in",
  $arrayContains: "array-contains",
  $arrayContainsAny: "array-contains-any",
} as const;

type SupportedOperator = keyof typeof operatorMap;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isOperatorObject(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).some((k) => OP_KEYS.has(k as any));
}

function assert(condition: any, msg: string): asserts condition {
  if (!condition) throw new Error(msg);
}

function omitLogicalKeys<T extends Record<string, any>>(obj: T): T {
  const clone: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === "$or" || k === "$and" || k === "$nor") continue;
    clone[k] = v;
  }
  return clone as T;
}

// ---------------- Builder ----------------

export class FilterQueryBuilder<T extends Record<string, any>> {
  // Keep composite/field filters separate from non-filter constraints
  private filterConstraints: QueryFilterConstraint[] = [];
  private otherConstraints: QueryConstraint[] = [];

  constructor(private collection: CollectionReference<T>) {}

  whereFieldEquals<K extends keyof T>(field: K, value: T[K]): this {
    this.filterConstraints.push(where(field as string, "==", value as any));
    return this;
  }

  greaterThan<K extends keyof T>(field: K, value: T[K]): this {
    this.filterConstraints.push(where(field as string, ">", value as any));
    return this;
  }

  limit(num: number): this {
    this.otherConstraints.push(limit(num));
    return this;
  }

  afterAt(docSnap: DocumentData): this {
    this.otherConstraints.push(startAfter(docSnap));
    return this;
  }

  beforeAt(docSnap: DocumentData): this {
    this.otherConstraints.push(endBefore(docSnap), limitToLast(10));
    return this;
  }

  orderBy<K extends keyof T | "id">(field: K, direction: OrderByDirection = "asc"): this {
    const fieldPath = field === "id" ? documentId() : (field as string);
    this.otherConstraints.push(orderBy(fieldPath, direction));
    return this;
  }

  /**
   * Accepts the new FilterFields<T> and turns it into constraints.
   */
  whereFields(fields: FilterFields<T>): this {
    const composite = this.buildCompositeConstraint(fields); // QueryFilterConstraint | null
    if (composite) this.filterConstraints.push(composite);
    return this;
  }

  /**
   * Optional helper to build simple ORs of equality conditions (back-compat).
   * Returns a composite filter constraint and stores it in filterConstraints.
   */
  or(conditions: Partial<T>[]): this {
    const groups: QueryFilterConstraint[] = conditions.map((cond) =>
      andFilter(
        ...Object.entries(cond)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => where(k as string, "==", v))
      )
    );
    this.filterConstraints.push(orFilter(...groups));
    return this;
  }

  build(): Query<T> {
    // Some Firebase versions don't type composite filters as QueryConstraint.
    // We keep filters separate and cast only at the call site.
    const allConstraints = [...(this.filterConstraints as unknown as QueryConstraint[]), ...this.otherConstraints];
    return query(this.collection, ...allConstraints);
  }

  // ---------------- internals ----------------

  private buildCompositeConstraint(block: FilterFields<T>): QueryFilterConstraint | null {
    if (block.$nor) {
      throw new Error(
        "Firestore does not support $nor. Consider rewriting using allowed operators (!=, not-in) or restructuring your query."
      );
    }

    if (Array.isArray(block.$or) && block.$or.length > 0) {
      const groups: QueryFilterConstraint[] = block.$or
        .map((group) => this.buildAndGroup(group))
        .filter((g): g is QueryFilterConstraint => !!g);

      const rest = this.buildAndGroup(omitLogicalKeys(block));

      if (groups.length === 0) return rest ?? null;

      const orGroup = orFilter(...groups);
      return rest ? andFilter(rest, orGroup) : orGroup;
    }

    if (Array.isArray(block.$and) && block.$and.length > 0) {
      const groups: QueryFilterConstraint[] = block.$and
        .map((group) => this.buildAndGroup(group))
        .filter((g): g is QueryFilterConstraint => !!g);

      const rest = this.buildAndGroup(omitLogicalKeys(block));
      if (rest) groups.push(rest);

      if (groups.length === 0) return null;
      if (groups.length === 1) return groups[0];
      return andFilter(...groups);
    }

    return this.buildAndGroup(block);
  }

  private buildAndGroup(block: FilterFields<T>): QueryFilterConstraint | null {
    const entries = Object.entries(block).filter(([k]) => k !== "$or" && k !== "$and" && k !== "$nor") as [
      string,
      unknown
    ][];

    const leafConstraints: QueryFilterConstraint[] = [];
    for (const [path, val] of entries) {
      this.collectConstraintsForPath(path, val, leafConstraints);
    }

    if (leafConstraints.length === 0) return null;
    if (leafConstraints.length === 1) return leafConstraints[0];
    return andFilter(...leafConstraints);
  }

  private collectConstraintsForPath(path: string, value: unknown, out: QueryFilterConstraint[]) {
    if (value === undefined) return;

    const fieldPath = path === "id" ? documentId() : path;
    if (isPlainObject(value) && isOperatorObject(value)) {
      for (const [opKey, opVal] of Object.entries(value)) {
        if (opKey === "$regex") {
          throw new Error("Firestore doesn't support regex queries.");
        }
        if (opKey === "$exists") {
          assert(typeof opVal === "boolean", "$exists must be boolean");
          if (opVal) {
            out.push(where(fieldPath, "!=", null));
          } else {
            out.push(where(fieldPath, "==", null));
          }
          continue;
        }

        const mapped = operatorMap[opKey as SupportedOperator];
        assert(mapped, `Unsupported operator: ${opKey}`);

        if (opKey === "$arrayContains") {
          if (Array.isArray(opVal)) {
            assert(opVal.length === 1, "$arrayContains expects exactly 1 value");
            out.push(where(fieldPath, mapped as any, opVal[0]));
          } else {
            out.push(where(fieldPath, mapped as any, opVal as any));
          }
          continue;
        }

        if (opKey === "$arrayContainsAny" || opKey === "$in" || opKey === "$nin") {
          assert(Array.isArray(opVal), `${opKey} expects an array`);
          out.push(where(fieldPath, mapped as any, opVal as any[]));
          continue;
        }

        out.push(where(fieldPath, mapped as any, opVal as any));
      }
      return;
    }

    if (isPlainObject(value)) {
      for (const [childKey, childVal] of Object.entries(value)) {
        const childPath = `${path}.${childKey}`;
        this.collectConstraintsForPath(childPath, childVal, out);
      }
      return;
    }

    out.push(where(fieldPath, "==", value as any));
  }
}

// ---------------- Helper builder function ----------------

export const buildFilterQuery = async <T extends Record<string, any>>(
  queryOptions: FilterQuery<T> & {
    collection: CollectionReference<T>;
  }
): Promise<Query<T>> => {
  const { collection, limit: lim, offset, cursor, orderBy: order, where: whereBlock } = queryOptions;

  const builder = new FilterQueryBuilder<T>(collection);

  if (whereBlock) {
    builder.whereFields(whereBlock);
  }

  // Apply ordering first to ensure consistent results
  if (order) {
    const orderList = Array.isArray(order[0]) ? (order as FilterOrderBy<T>[]) : [order as FilterOrderBy<T>];
    orderList.forEach(([field, direction]) => {
      builder.orderBy(field, direction);
    });
  }
  
  // Apply cursor after ordering so it aligns with orderBy fields
  if (cursor?.docId) {
    const ref = doc(collection as unknown as CollectionReference<DocumentData>, cursor.docId) as DocumentReference<T>;
    const snap = await getDoc(ref);
    if (snap.exists()) {
      builder.afterAt(snap);
    }
  } else if (offset) {
    const ref = doc(collection as unknown as CollectionReference<DocumentData>, offset) as DocumentReference<T>;
    const snap = await getDoc(ref);
    if (snap.exists()) {
      builder.afterAt(snap);
    }
  }

  // Apply limit
  if (lim) builder.limit(lim);

  return builder.build();
};
